import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  [
    'flex items-center flex-row justify-center gap-2 justify-center text-sm lg:text-base font-semibold transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
    'ea-button',
  ],
  {
    variants: {
      variant: {
        default: ['bg-primary text-white hover:bg-primary/90', 'ea-button--default'],
        'primary-foreground': [
          'bg-primary-foreground text-primary hover:bg-primary-foreground/90',
          'ea-button--primary-foreground',
        ],
        destructive: [
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
          'ea-button--destructive',
        ],
        accent: [
          'bg-accent text-accent-foreground shadow-sm hover:bg-accent/90',
          'ea-button--accent',
        ],
        outline: [
          'border border-border bg-transparent text-black hover:bg-border/20',
          'ea-button--outline',
        ],
        secondary: ['bg-secondary text-black hover:bg-secondary/90', 'ea-button--secondary'],
        ghost: ['text-muted bg-transparent hover:bg-[#D3C7B8]', 'ea-button--ghost'],
        link: ['text-primary underline-offset-4 hover:underline', 'ea-button--link'],
        dark: [
          'border border-black hover:border-black/90 bg-black text-background hover:bg-black/90',
          'ea-button--dark',
        ],
        rating: ['text-rating bg-rating/20 hover:bg-rating/25'],
        white: ['text-black bg-white hover:bg-white/90'],
        none: ['bg-transparent p-0 m-0 shadow-none hover:bg-transparent', 'ea-button--none'],
        'default-alt': ['text-primary bg-primary/20  hover:bg-primary/25'],
        'destructive-alt': ['text-destructive bg-destructive/20 hover:bg-destructive/25'],
        'accent-alt': ['text-accent bg-accent/20 hover:bg-accent/25'],
      },
      rounded: {
        none: ['rounded-none', 'ea-button--rounded-none'],
        default: ['rounded-full', 'ea-button--rounded-default'],
      },
      size: {
        none: ['p-0 m-0 w-fit h-fit', 'ea-button--size-none'],
        default: ['h-12 px-4 sm:px-5 lg:px-6', 'ea-button--size-default'],
        xs: ['h-8 px-3 text-xs', 'ea-button--size-xs'],
        sm: ['h-10 px-6 text-sm', 'ea-button--size-sm'],
        lg: ['h-14 px-8', 'ea-button--size-lg'],
        'icon-md': ['min-w-10 max-w-10 min-h-10 max-h-10', 'ea-button--size-icon-md'],
        icon: ['min-w-12 max-w-12 max-h-12 min-h-12', 'ea-button--size-icon'],
        'icon-sm': ['h-7 w-7', 'ea-button--size-icon-sm'],
        'icon-xs': ['h-5 w-5', 'ea-button--size-icon-xs'],
        'icon-lg': [
          'min-w-8 max-w-8 min-h-8 max-h-8 sm:min-w-10 sm:max-w-10 sm:min-h-10 sm:max-h-10 lg:min-w-12 lg:max-w-12 lg:min-h-12 lg:max-h-12',
          'ea-button--size-icon-lg',
        ],
        'icon-xl': [
          'max-h-[52px] max-w-[52px] min-h-[52px] min-w-[52px]',
          'ea-button--size-icon-xl',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
      rounded: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, isLoading, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const blocked = Boolean(props.disabled || isLoading);

    // Keep the original DOM/content in flow: no wrapper may change flex gaps,
    // responsive labels, intrinsic width, or the accessible name during loading.
    const loadingContent = (label: React.ReactNode) => (
      <>
        {label}

        {isLoading && (
          <span
            data-ea-button-spinner=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            style={{ margin: 0 }}
          >
            <Spinner />
          </span>
        )}
      </>
    );
    const content =
      asChild && React.isValidElement<React.HTMLAttributes<HTMLElement>>(children)
        ? React.cloneElement(children, {
            children: loadingContent(children.props.children),
            ...(isLoading && {
              style: { ...children.props.style, WebkitTextFillColor: 'transparent' },
            }),
            // Native disabled does not disable links. Inert blocks pointer and
            // keyboard activation without adding client-only event handlers.
            ...(blocked && {
              tabIndex: -1,
              inert: true,
            }),
          })
        : asChild
          ? children
          : loadingContent(children);
    return (
      <Comp
        className={cn(
          isLoading && 'relative [&>*:not([data-ea-button-spinner])]:opacity-0',
          cn(buttonVariants({ variant, size, rounded, className })),
        )}
        ref={ref}
        {...props}
        style={isLoading ? { ...props.style, WebkitTextFillColor: 'transparent' } : props.style}
        disabled={blocked}
        aria-disabled={blocked}
        aria-busy={isLoading || props['aria-busy']}
        data-ea-button-loading={isLoading || undefined}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
